import React from "react"
import { Platform, StyleSheet } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

import { FarmHqLogo } from "@/components/FarmHqLogo"
import useHeaderSize from "@/hooks/useHeaderSize"
import { ScrollView, testIds, Theme } from "@/theme"
import { ScreenHeader } from "@/theme/ScreenHeader"

import Form from "./Form"

import type { AuthPageId } from "@/redux/auth.reducer"

import type { Edge } from "react-native-safe-area-context"
import { FocusTrackingStatusBar } from "@/components/FocusTrackingStatusBar"

interface StaticProps {
  id: AuthPageId
  titleText: string
}

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: "stretch",
    justifyContent: "center",
    marginHorizontal: "auto",
    maxWidth: Theme.SIZES.$lg,
    padding: Theme.SPACING.$4,
    width: "100%",
  },
  logoWeb: {
    height: 150,
    marginVertical: Theme.SPACING.$8,
    paddingVertical: Theme.SPACING.$4,
  },
  root: {
    flex: 1,
  },
})

const edges: Edge[] = [
  //
  "left",
  //
  "right",
  //
  "bottom",
  //
  "top",
]

export function AuthFormLayout({
  children,
  id,
  titleText,
  ...rest
}: React.PropsWithChildren<StaticProps>) {
  const headerHeight = useHeaderSize()

  return (
    <SafeAreaView {...testIds(id)} edges={edges} style={styles.root}>
      <FocusTrackingStatusBar />
      <Form {...rest}>
        <ScrollView
          contentContainerStyle={[
            styles.contentContainer,
            {
              paddingTop: Platform.select({
                // android: 20,
                android: 0,
                ios: headerHeight + 20,
                web: 0,
              }),
            },
          ]}
        >
          {Platform.OS === "web" ? (
            <React.Fragment>
              <FarmHqLogo
                name={"farmhq-logo-stacked.png"}
                resizeMode={"contain"}
                style={styles.logoWeb}
              />
              <ScreenHeader headingVariant={"h2"} titleText={titleText} />
            </React.Fragment>
          ) : null}
          {children}
        </ScrollView>
      </Form>
    </SafeAreaView>
  )
}

export default function withAuthFormLayout(
  Component: React.FC,
  staticProps: StaticProps,
) {
  const Wrapped: React.FC = (props) => (
    <AuthFormLayout {...staticProps} {...props}>
      <Component />
    </AuthFormLayout>
  )
  Wrapped.displayName = staticProps.id

  return Wrapped
}
